/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
    IAgentPlugin,
    IPluginMethodMap,
  } from "@vlinder-web/plugin-agent-service-react";
  import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
  import { GET_EVENT_TIMELINE } from "../graphql";
  import { VApplication } from "../../../application";
  import { useEventId } from "../provider";
  import { toHeaderCase } from "js-convert-case";
  import config from "../config/eventTimeline.config.json";
  import moment from "moment";
  import jwt_decode from "jwt-decode";
  
  import { AppNavigatorService } from "../../appNavigatorService";
  
  const getWhoamiSrv = () => {
    const app = VApplication.getInstance();
    return app.getSync("services.AppNavigatorService") as AppNavigatorService;
  };
  
  export interface IEventTimelineMethods extends IPluginMethodMap {
    getEventTimelineProps(args: any): Promise<any>;
  }
  
  const getGraphQLModule = () => {
    const app = VApplication.getInstance();
    return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
  };
  
  const lookup = require("country-code-lookup");
  
  export class EventTimelineService implements IAgentPlugin {
    readonly methods: IEventTimelineMethods;
  
    constructor() {
      this.methods = {
        getEventTimelineProps: this.getProps.bind(this),
      };
    }
  
    async getProps(args: any) {
      const { eventId } = useEventId();
  
      const graphqlModule = getGraphQLModule();
      const client = graphqlModule.getClient("graphql-gateway");
      try {
        if (client) {
          const res = await client.query({
            query: GET_EVENT_TIMELINE,
            variables: {
                "input": {
                  "where": [
                    {
                      "column": "txnSetId",
                      "value": eventId,
                      "op": "Equal"
                    },
                    {
                      "column": "type",
                      "value": "CYCLONE_OCCURRENCE",
                      "op": "Equal"
                    }
                  ]
                }
            },
            fetchPolicy: "network-only",
          });
          if (res?.data?.getTxnList) {
            console.log(`event transaction details`, res?.data?.getTxnList);
            return {
              timeline:this.getTimeLine(res?.data?.getTxnList?.data)
            };
          }
        }
      } catch (err: any) {
        console.log(`Sign In user error`, err?.response?.data);
        throw Error(err?.response?.data?.error?.display_message);
      }
      // return {
      //   timeline:config
      // }
    }
  
    private getTimeLine(data: any) {
      let _temp: any = [];
      Array.isArray(data) && data?.forEach(element => {
          _temp.push(
              {
                  "key": element?.id,
                  "headline": toHeaderCase(element?.action ?? ""),
                  "headlineBackgroundColor": "#579783",
                  "children": this.getChildren(element)
              }
          )
      });
      return _temp;
  }

  private getChildren(data: any) {
    let _temp: any = [];
    _temp.push({
        "itemId": data?.id,
        "title": toHeaderCase(data?.payload?.stormName ? data?.payload?.stormName: data?.payload?.payoutEstimate ? "Payout Estimate":data?.payload?.status ?? "-"),
        multiColList: this.getProvenanceAccordion(data),
        "subtitle": data?.payload?.date
        ? moment(data?.payload?.date).format("LLL")
        : data?.payload?.payoutEstimate ?? null,
    });
    return _temp;
  }

  private getProvenanceAccordion(res: any) {
    let _temp: any = [];
    if (res) {
      let _list: any = [];
      let _date: any = [];
      let _location: any = [];
      if (res?.payload?.category) {
        _list.push({
          title: toHeaderCase("storm strength"),
          subTitle: `${toHeaderCase(res?.payload?.category)}`,
          icon: config?.category
        })
      }
      if(res?.payload?.exposure){
        _location.push({
          title: toHeaderCase("exposer"),
            subTitle: toHeaderCase(res?.payload?.exposure) ?? "-",
            icon: config?.exposure
        })
      }
      if (res?.payload?.vmax) {
          _date.push({
            title: toHeaderCase("Max. wind speed"),
            subTitle: res?.payload?.vmax ? `${res?.payload?.vmax} m/s`:"-",
            icon: config?.wind
          })
      }
      if (res?.payload?.vmin) {
        _date.push({
          title: toHeaderCase("Min. wind speed"),
          subTitle: res?.payload?.vmin ? `${res?.payload?.vmin} m/s`:"-",
          icon: config?.wind
        })
      }
        if (res?.payload?.totalPolicies) {
          _date.push({
            title: toHeaderCase("Eligible Policies"),
            subTitle: res?.payload?.totalPolicies,
            icon: config?.user
          })
    }
    if (res?.performedBy) {
      _date.push({
        title: toHeaderCase("by"),
        subTitle: `${toHeaderCase(res?.performedBy?.firstName ?? "")} ${toHeaderCase(res?.performedBy?.lastName ?? "")}`,
        icon: config?.orgIcon
      })
}
      _temp = [..._list, ..._location, ..._date]
    }
    return {
      "numberOfColumns": "2",
      data: _temp
    };
  }
  }
  